import { Injectable, inject } from '@angular/core';
import { NvgLayer, SandySoilTypeLayer } from '@next/arable-lib/geo';
import { GroundWaterProtectionLayer } from '@next/arable-lib/geo';
import { SoilTypeFertilizerLawLayer } from '@next/arable-lib/geo';
import { WurSoilTypes } from '@next/arable-lib/shared';
import { Feature } from 'ol';
import { Extent } from 'ol/extent';
import { FeatureLike } from 'ol/Feature';
import { Geometry } from 'ol/geom';

import { forkJoin, Observable } from 'rxjs';
import { LayerData } from 'src/app/shared/models';

import { JstsHelperService } from 'src/app/shared/services/jsts-helper.service';

import { GeoLayerDataService } from './geoLayerData.service';
import { IntersectGeometryService } from '../../shared/services/intersect-geometry.service';

enum LayerPropertyName {
  soilTypeFertilizerLaw = 'HOOFDGRS',
  sandySoilLaw = 'GRONDS',
  any = 'any',
}

@Injectable()
export class GeoLayerDataBrowserService implements GeoLayerDataService {
  private readonly intersectGeometryService = inject(IntersectGeometryService);
  private readonly soilTypeFertilizerLawLayer = inject(SoilTypeFertilizerLawLayer);
  private readonly sandySoilTypeLayer = inject(SandySoilTypeLayer);
  private readonly groundWaterProtectionLayer = inject(GroundWaterProtectionLayer);
  private readonly jstsHelper = inject(JstsHelperService);
  private readonly nvgLayer = inject(NvgLayer);

  getFeatureData(feature: Feature): LayerData {
    const returnValue: LayerData = {};

    if (!feature) {
      return returnValue;
    }

    const featureGeometry = feature.getGeometry();

    if (!featureGeometry) {
      return returnValue;
    }

    returnValue.soilTypeFertilizerLaw = this.getSoilTypeFertilizerLaw(featureGeometry);
    returnValue.sandySoilLaw = this.getSandySoilLaw(featureGeometry);
    returnValue.isGroundWaterPreservation = this.getGroundWaterPreservation(featureGeometry);
    returnValue.isNutrientsPollutedArea = this.getNvg(featureGeometry);

    return returnValue;
  }

  loadLayerFeaturesInExtent(extent: Extent): Observable<boolean[]> {
    return forkJoin([
      this.groundWaterProtectionLayer.loadLayerFeaturesInExtent(extent),
      this.sandySoilTypeLayer.loadLayerFeaturesInExtent(extent),
      this.soilTypeFertilizerLawLayer.loadLayerFeaturesInExtent(extent),
    ]);
  }

  getSoilTypeFertilizerLaw(featureGeometry: Geometry): string {
    const soilTypesFertilizerLaw = this.soilTypeFertilizerLawLayer.layer
      .getSource()
      .getFeaturesInExtent(featureGeometry.getExtent());
    const intersectingFeatures: FeatureLike[] = this.getIntersectingFeatures(
      featureGeometry,
      soilTypesFertilizerLaw,
    );

    return this.getLayer(
      featureGeometry,
      intersectingFeatures,
      75,
      LayerPropertyName.soilTypeFertilizerLaw,
    );
  }

  getSandySoilLaw(featureGeometry: Geometry): WurSoilTypes {
    const sandySoilLaw = this.sandySoilTypeLayer.layer
      .getSource()
      .getFeaturesInExtent(featureGeometry.getExtent());
    const intersectingFeatures: FeatureLike[] = this.getIntersectingFeatures(
      featureGeometry,
      sandySoilLaw,
    );

    return this.getLayer(
      featureGeometry,
      intersectingFeatures,
      0,
      LayerPropertyName.sandySoilLaw,
      WurSoilTypes.Sand,
    ) as unknown as WurSoilTypes;
  }

  getGroundWaterPreservation(featureGeometry: Geometry): boolean {
    const layerFeatures = this.groundWaterProtectionLayer.layer
      .getSource()
      .getFeaturesInExtent(featureGeometry.getExtent());
    const intersectingFeatures: FeatureLike[] = this.getIntersectingFeatures(
      featureGeometry,
      layerFeatures,
    );

    return intersectingFeatures.length > 0;
  }

  getNvg(featureGeometry: Geometry): boolean {
    const layerFeatures = this.nvgLayer.layer
      .getSource()
      .getFeaturesInExtent(featureGeometry.getExtent());
    const intersectingFeatures: FeatureLike[] = this.getIntersectingFeatures(
      featureGeometry,
      layerFeatures,
    );
    let intersectingPercentage = 0;
    intersectingFeatures.forEach((intersectingFeature) => {
      intersectingPercentage += this.getIntersectPercentage(featureGeometry, intersectingFeature);
    });
    return intersectingPercentage > 50;
  }

  getLayer(
    featureGeometry: Geometry,
    intersectingFeatures: FeatureLike[],
    minPercentage: number,
    propertyName: LayerPropertyName,
    propertyValue?: number | string,
  ): string {
    let layerWithMostOverlap: string;
    let biggestPercentage = 0;

    if (intersectingFeatures.length === 0) {
      return;
    }

    for (const intersectingFeature of intersectingFeatures) {
      const intersectPercentage = this.getIntersectPercentage(featureGeometry, intersectingFeature);
      const layerName = intersectingFeature.getProperties()[propertyName] as string;
      if (
        intersectPercentage >= biggestPercentage &&
        (!propertyValue || propertyValue === layerName)
      ) {
        biggestPercentage = intersectPercentage;
        layerWithMostOverlap = layerName;
      }
    }
    return !minPercentage || biggestPercentage >= minPercentage ? layerWithMostOverlap : undefined;
  }

  getIntersectingFeatures(featureGeometry: Geometry, layerFeatures: FeatureLike[]) {
    const intersectingFeatures: FeatureLike[] = [];
    layerFeatures.forEach((feature) => {
      const isIntersecting = this.jstsHelper.safeIntersectsByParsing(
        featureGeometry,
        feature.getGeometry() as Geometry,
      );

      if (isIntersecting) {
        intersectingFeatures.push(feature);
      }
    });
    return intersectingFeatures;
  }

  getIntersectPercentage(featureGeometry: Geometry, intersectingFeature: FeatureLike): number {
    return this.intersectGeometryService.getIntersectPercentage(
      featureGeometry,
      intersectingFeature.getGeometry() as Geometry,
    );
  }
}
